import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { PRICING_BUILD, PRICING_CARE } from "../mock";

const choose = (prefill) => {
  window.dispatchEvent(new CustomEvent("prefill-contact", { detail: prefill }));
  const el = document.querySelector("#kontakt");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const PlanCard = ({ p, dark }) => {
  // dark = the block sits on a dark panel. isCardDark = this specific card has a dark surface.
  const isCardDark = dark ? !p.highlighted : p.highlighted;

  const surface = isCardDark
    ? p.highlighted
      ? "bg-[#0e0e0d]"
      : "bg-[#171714] border border-white/10"
    : "bg-white border border-black/[0.06]";
  const ring = p.highlighted ? (isCardDark ? "ring-1 ring-[#d8ff00]/45" : "ring-1 ring-[#d8ff00]") : "";
  const shadow = p.highlighted ? "shadow-[0_28px_70px_rgba(0,0,0,0.16)]" : "";

  const nameColor = p.highlighted && isCardDark ? "text-[#d8ff00]" : isCardDark ? "text-white/70" : "text-[#8b8b84]";
  const priceColor = isCardDark ? "text-white" : "text-[#0e0e0d]";
  const noteFor = isCardDark ? "text-white/55" : "text-[#8b8b84]";
  const descColor = isCardDark ? "text-white/70" : "text-[#5f5f59]";
  const featColor = isCardDark ? "text-white/85" : "text-[#3d3d38]";
  const smallNote = isCardDark ? "text-white/45" : "text-[#9a9a92]";
  const ctaClass = p.highlighted ? "btn-lime" : isCardDark ? "btn-outline-light" : "btn-outline-ink";

  return (
    <div
      data-testid={`pricing-card-${p.id}`}
      className={`reveal relative rounded-[28px] p-8 md:p-9 flex flex-col transition-transform duration-500 hover:-translate-y-1.5 ${surface} ${ring} ${shadow}`}
    >
      {p.badge && (
        <span
          data-testid={`pricing-badge-${p.id}`}
          className="absolute -top-3 left-8 text-[11px] px-3 py-1 rounded-full bg-[#d8ff00] text-[#0e0e0d] font-semibold tracking-[0.08em] uppercase"
        >
          {p.badge}
        </span>
      )}

      <p className={`eyebrow ${nameColor}`}>{p.name}</p>

      <div className="mt-5 flex items-end gap-1.5">
        <span className={`display text-3xl md:text-[2.4rem] leading-none ${priceColor}`}>{p.price}</span>
        {p.priceNote && <span className={`text-sm mb-1 ${noteFor}`}>{p.priceNote}</span>}
      </div>

      <p className={`text-sm mt-4 leading-relaxed ${descColor}`}>{p.desc}</p>

      <ul className="mt-7 space-y-3 flex-1">
        {p.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm">
            <span className="grid place-items-center w-5 h-5 rounded-full shrink-0 mt-0.5 bg-[#d8ff00] text-[#0e0e0d]">
              <Check size={11} strokeWidth={3} />
            </span>
            <span className={featColor}>{f}</span>
          </li>
        ))}
      </ul>

      {p.note && <p className={`text-xs mt-6 leading-relaxed ${smallNote}`}>{p.note}</p>}

      <button
        onClick={() => choose(p.prefill)}
        data-testid={`pricing-cta-${p.id}`}
        className={`mt-8 py-3.5 px-6 text-sm ${ctaClass}`}
      >
        {p.cta} <ArrowRight size={16} />
      </button>
    </div>
  );
};

const PricingBlock = ({ data, dark }) => (
  <div>
    <div className="reveal max-w-2xl">
      <p className={`eyebrow ${dark ? "text-[#d8ff00]/80" : "text-[#8b8b84]"}`}>{data.eyebrow}</p>
      <h2 className={`display text-3xl sm:text-4xl lg:text-[3.25rem] mt-4 ${dark ? "text-white" : "text-[#0e0e0d]"}`}>
        {data.title}
      </h2>
      <p className={`mt-6 leading-relaxed ${dark ? "text-[#a5a59d]" : "text-[#5f5f59]"}`}>{data.subtitle}</p>
    </div>
    <div className="mt-12 grid md:grid-cols-3 gap-5 md:gap-6 items-start">
      {data.plans.map((p) => (
        <PlanCard key={p.id} p={p} dark={dark} />
      ))}
    </div>
  </div>
);

export default function Pricing() {
  return (
    <section id="cenik" className="bg-[#f5f5f2] py-24 md:py-32" data-testid="pricing-section">
      <div className="container-x">
        <PricingBlock data={PRICING_BUILD} dark={false} />
      </div>

      <div className="container-x mt-20 md:mt-28">
        <div className="bg-[#0e0e0d] rounded-[36px] md:rounded-[44px] px-6 py-16 md:px-14 md:py-20" data-testid="pricing-care-panel">
          <PricingBlock data={PRICING_CARE} dark={true} />
        </div>
      </div>
    </section>
  );
}
